import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Icon, statusMeta } from "@/lib/ui";
import { Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogClose,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const PRIVACY = [
  { v: "always", label: "Always show my number", desc: "Finder can always see and call you." },
  { v: "lost_only", label: "Show my number only when lost", desc: "Hidden while active, visible in lost mode." },
  { v: "masked", label: "Keep my number private", desc: "Finder sends a message; you stay anonymous." },
];

export default function TagManage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tag, setTag] = useState(null);
  const [themes, setThemes] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [scans, setScans] = useState([]);
  const [docs, setDocs] = useState([]);

  const loadDocs = useCallback(() => {
    api.get(`/tags/${id}/documents`).then((r) => setDocs(r.data.data)).catch(() => {});
  }, [id]);

  const load = useCallback(async () => {
    const { data } = await api.get(`/tags/${id}`);
    setTag(data.data);
    api.get(`/themes?category_id=${data.data.category_id}`).then((r) => setThemes(r.data.data)).catch(() => {});
    api.get(`/tags/${id}/messages`).then((r) => setMsgs(r.data.data)).catch(() => {});
    api.get(`/tags/${id}/scans`).then((r) => setScans(r.data.data)).catch(() => {});
    loadDocs();
  }, [id, loadDocs]);

  useEffect(() => { load().catch(() => nav("/app")); }, [load, nav]);

  if (!tag) return <div className="py-24 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const meta = statusMeta(tag.status);
  const s = tag.settings || {};

  const saveSettings = async (patch, msg = "Saved") => {
    try {
      const { data } = await api.put(`/tags/${id}/settings`, patch);
      setTag((t) => ({ ...t, ...data.data, settings: data.data.settings }));
      toast.success(msg);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="pb-8">
      <div className="px-5 pt-6 flex items-center justify-between">
        <Link to="/app" className="tap inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground">
          <Icon name="ArrowLeft" className="w-4 h-4" /> My Tags
        </Link>
        <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: meta.bg, color: meta.color }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.color }} /> {meta.label}
        </span>
      </div>

      <div className="px-5 mt-5 flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl grid place-items-center shrink-0" style={{ backgroundColor: meta.bg, color: meta.color }}>
          <Icon name={tag.category?.icon || "Tag"} className="w-7 h-7" />
        </div>
        <div>
          <h1 className="font-display font-black text-2xl tracking-tight">{tag.category?.name} Tag</h1>
          <p className="text-sm text-muted-foreground font-mono">{tag.tag_number}</p>
        </div>
      </div>

      {/* Lost / Found actions */}
      <div className="px-5 mt-6">
        {tag.status === "LOST" ? (
          <FoundDialog onConfirm={async () => { await api.post(`/tags/${id}/found`); toast.success("Marked as found!"); load(); }} />
        ) : (
          <LostDialog current={s} onConfirm={async (payload) => { await api.post(`/tags/${id}/lost`, payload); toast.success("Marked as lost."); load(); }} />
        )}
      </div>

      <div className="px-5 mt-6">
        <Tabs defaultValue="settings">
          <TabsList className="w-full grid grid-cols-4 rounded-full h-11 bg-secondary">
            <TabsTrigger value="settings" data-testid="tab-settings" className="rounded-full text-xs">Settings</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents" className="rounded-full text-xs">{docLabel(tag.category?.name)}</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages" className="rounded-full text-xs">Messages{msgs.length ? ` (${msgs.length})` : ""}</TabsTrigger>
            <TabsTrigger value="scans" data-testid="tab-scans" className="rounded-full text-xs">Scans</TabsTrigger>
          </TabsList>

          {/* SETTINGS */}
          <TabsContent value="settings" className="mt-5 space-y-6">
            <Section title="Contact privacy">
              <RadioGroup value={s.contact_privacy_mode || "masked"} onValueChange={(v) => saveSettings({ contact_privacy_mode: v })}>
                {PRIVACY.map((p) => (
                  <label key={p.v} className="flex items-start gap-3 rounded-xl border border-border p-3.5 tap cursor-pointer" data-testid={`privacy-${p.v}`}>
                    <RadioGroupItem value={p.v} className="mt-0.5" />
                    <div>
                      <p className="font-semibold text-sm">{p.label}</p>
                      <p className="text-xs text-muted-foreground">{p.desc}</p>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </Section>

            <Section title="Contact details">
              <EditableField label="Owner display name" value={s.owner_name || ""} onSave={(v) => saveSettings({ owner_name: v })} testid="owner-name" />
              <EditableField label="Contact number" value={s.owner_phone || ""} onSave={(v) => saveSettings({ owner_phone: v })} testid="owner-phone" />
              <EditableField label="Public message" value={s.public_message || ""} onSave={(v) => saveSettings({ public_message: v })} testid="public-message" textarea />
            </Section>

            <Section title="Finder options">
              <Toggle label="Show my name to finder" checked={!!s.show_owner_name} onChange={(v) => saveSettings({ show_owner_name: v })} testid="toggle-name" />
              <Toggle label="Allow finder messages" checked={s.allow_finder_message !== false} onChange={(v) => saveSettings({ allow_finder_message: v })} testid="toggle-message" />
              <Toggle label="Allow WhatsApp contact" checked={!!s.allow_whatsapp_contact} onChange={(v) => saveSettings({ allow_whatsapp_contact: v })} testid="toggle-whatsapp" />
              <Toggle label="Notify me on every scan" checked={!!s.notify_on_scan} onChange={(v) => saveSettings({ notify_on_scan: v })} testid="toggle-scan" />
            </Section>

            <Section title="Theme">
              <div className="grid grid-cols-2 gap-3">
                {themes.map((th) => {
                  const active = s.theme_id === th.id;
                  return (
                    <button key={th.id} data-testid={`theme-${th.name}`} onClick={() => saveSettings({ theme_id: th.id }, "Theme updated")}
                      className={`tap rounded-xl border p-3 text-left transition-colors ${active ? "border-primary ring-2 ring-primary" : "border-border"}`}>
                      <span className="block w-full h-10 rounded-lg mb-2" style={{ backgroundColor: th.accent }} />
                      <span className="font-semibold text-sm">{th.name}</span>
                      {active && <span className="block text-xs text-status-active font-semibold">Selected</span>}
                    </button>
                  );
                })}
              </div>
            </Section>
          </TabsContent>

          {/* DOCUMENTS */}
          <TabsContent value="documents" className="mt-5">
            <DocumentsPanel tagId={id} docs={docs} reload={loadDocs} category={tag.category?.name} />
          </TabsContent>

          {/* MESSAGES */}
          <TabsContent value="messages" className="mt-5 space-y-3">
            {msgs.length === 0 ? <Empty icon="Inbox" text="No finder messages yet." /> :
              msgs.map((m) => (
                <div key={m.id} className="rounded-2xl border border-border p-4" data-testid="finder-message-item">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold">{m.finder_name}</p>
                    <span className="text-xs text-muted-foreground">{fmt(m.created_at)}</span>
                  </div>
                  <p className="text-sm mt-1.5">{m.message}</p>
                  <div className="flex items-center gap-4 mt-3">
                    <a href={`tel:${m.finder_phone}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-status-active">
                      <Icon name="Phone" className="w-3.5 h-3.5" /> {m.finder_phone}
                    </a>
                    {m.maps_url && (
                      <a href={m.maps_url} target="_blank" rel="noreferrer" data-testid="message-location" className="inline-flex items-center gap-1.5 text-sm font-semibold text-status-found">
                        <Icon name="MapPin" className="w-3.5 h-3.5" /> View location
                      </a>
                    )}
                  </div>
                </div>
              ))}
          </TabsContent>

          {/* SCANS */}
          <TabsContent value="scans" className="mt-5 space-y-3">
            {scans.length === 0 ? <Empty icon="Radar" text="No scans recorded yet." /> :
              scans.map((sc, i) => (
                <div key={i} className="rounded-xl border border-border p-3.5 flex items-center gap-3">
                  <Icon name="Smartphone" className="w-4 h-4 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold">{sc.device_type} · {sc.browser}</p>
                    <p className="text-xs text-muted-foreground">{fmt(sc.created_at)}</p>
                  </div>
                </div>
              ))}
          </TabsContent>

          {/* QR */}
        </Tabs>
      </div>
    </div>
  );
}

function docLabel(category) {
  if ((category || "").toLowerCase() === "pets") return "Vax & Docs";
  return "Documents";
}

function DocumentsPanel({ tagId, docs, reload, category }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const isPet = (category || "").toLowerCase() === "pets";

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name);
      await api.post(`/tags/${tagId}/documents`, fd);
      toast.success("Document uploaded");
      reload();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/tags/${tagId}/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) { toast.error(apiErr(err)); }
  };

  const remove = async (d) => {
    try { await api.delete(`/tags/${tagId}/documents/${d.id}`); toast.success("Deleted"); reload(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {isPet ? "Store vaccination records and pet documents" : "Store warranty & bill for this item"} — up to 2 files (PDF or image, max 10MB).
      </p>
      {docs.map((d) => (
        <div key={d.id} className="rounded-2xl border border-border p-4 flex items-center gap-3" data-testid="document-item">
          <div className="w-10 h-10 rounded-xl bg-secondary grid place-items-center shrink-0">
            <Icon name={d.content_type === "application/pdf" ? "FileText" : "Image"} className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm truncate">{d.title || d.original_filename}</p>
            <p className="text-xs text-muted-foreground">{(d.size / 1024).toFixed(0)} KB</p>
          </div>
          <button onClick={() => download(d)} data-testid="doc-download" className="tap px-3 py-2 rounded-lg border border-border font-semibold text-sm">View</button>
          <button onClick={() => remove(d)} data-testid="doc-delete" className="tap px-2.5 py-2 rounded-lg border border-border text-status-lost"><Icon name="Trash2" className="w-4 h-4" /></button>
        </div>
      ))}
      {docs.length < 2 && (
        <>
          <input ref={inputRef} type="file" accept="application/pdf,image/*" onChange={upload} className="hidden" data-testid="doc-file-input" />
          <button data-testid="doc-upload-btn" disabled={busy} onClick={() => inputRef.current?.click()}
            className="tap w-full rounded-2xl border-2 border-dashed border-border py-8 flex flex-col items-center gap-2 hover:bg-secondary/50 disabled:opacity-60">
            {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Icon name="Upload" className="w-6 h-6" />}
            <span className="font-bold text-sm">{busy ? "Uploading…" : "Upload document"}</span>
          </button>
        </>
      )}
      {docs.length === 0 && !busy && <p className="text-xs text-muted-foreground text-center">No documents yet.</p>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground mb-3">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-border p-3.5">
      <span className="font-semibold text-sm">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

function EditableField({ label, value, onSave, testid, textarea }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  const changed = v !== value;
  return (
    <div className="rounded-xl border border-border p-3.5">
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      {textarea ? (
        <textarea data-testid={testid} value={v} onChange={(e) => setV(e.target.value)} rows={2} className="w-full mt-1 bg-transparent resize-none focus:outline-none font-medium" />
      ) : (
        <input data-testid={testid} value={v} onChange={(e) => setV(e.target.value)} className="w-full mt-1 bg-transparent focus:outline-none font-medium" />
      )}
      {changed && (
        <button onClick={() => onSave(v)} data-testid={`${testid}-save`} className="tap mt-2 text-xs font-bold text-status-active">Save</button>
      )}
    </div>
  );
}

function LostDialog({ current, onConfirm }) {
  const [msg, setMsg] = useState(current.lost_message || "Please contact me if you found this item.");
  const [mode, setMode] = useState(current.contact_privacy_mode || "lost_only");
  const [busy, setBusy] = useState(false);
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid="mark-lost-btn" className="tap w-full h-14 rounded-full bg-status-lost text-white font-bold flex items-center justify-center gap-2">
          <Icon name="TriangleAlert" className="w-5 h-5" /> Mark as Lost
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl max-w-[420px]">
        <DialogHeader><DialogTitle className="font-display text-2xl">Mark tag as lost</DialogTitle></DialogHeader>
        <label className="block">
          <span className="text-sm font-semibold">Lost message</span>
          <textarea data-testid="lost-message-input" value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} className="mt-1.5 w-full p-3 rounded-xl border border-input resize-none focus:outline-none focus:ring-2 focus:ring-ring" />
        </label>
        <div>
          <span className="text-sm font-semibold">Contact visibility</span>
          <RadioGroup value={mode} onValueChange={setMode} className="mt-2">
            {PRIVACY.map((p) => (
              <label key={p.v} className="flex items-center gap-3 rounded-xl border border-border p-3 tap cursor-pointer">
                <RadioGroupItem value={p.v} />
                <span className="text-sm font-medium">{p.label}</span>
              </label>
            ))}
          </RadioGroup>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <button data-testid="confirm-lost-btn" disabled={busy} onClick={async () => { setBusy(true); await onConfirm({ lost_message: msg, contact_privacy_mode: mode }); setBusy(false); }}
              className="tap w-full h-13 py-3.5 rounded-full bg-status-lost text-white font-bold">Confirm — Mark as Lost</button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FoundDialog({ onConfirm }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid="mark-found-btn" className="tap w-full h-14 rounded-full bg-status-found text-white font-bold flex items-center justify-center gap-2">
          <Icon name="CircleCheck" className="w-5 h-5" /> Mark as Found
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl max-w-[380px]">
        <DialogHeader><DialogTitle className="font-display text-2xl">Recovered?</DialogTitle></DialogHeader>
        <p className="text-muted-foreground">Have you recovered this item? The public page will return to normal.</p>
        <DialogFooter className="flex-col gap-2">
          <DialogClose asChild>
            <button data-testid="confirm-found-btn" onClick={onConfirm} className="tap w-full h-13 py-3.5 rounded-full bg-status-found text-white font-bold">Yes, Mark as Found</button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Empty({ icon, text }) {
  return (
    <div className="py-14 text-center">
      <Icon name={icon} className="w-9 h-9 mx-auto text-muted-foreground" />
      <p className="text-sm text-muted-foreground mt-2">{text}</p>
    </div>
  );
}

function fmt(iso) {
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
